import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash, faRotateLeft, faHourglassHalf, faCheckDouble, faSpinner
} from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import GroupIcon from './GroupIcon';
import { deadlineLabel } from '../../utils/groupExpiry';

/**
 * The Inactive tab — the call groups nobody came back to, and what to do with
 * them. ADMIN only; the tab is not offered to anyone else and the endpoints
 * behind it refuse everyone else.
 *
 * A group call makes a group for itself whether or not anyone wanted one
 * (server/utils/callSignalling.js), so a busy week leaves a drift of threads
 * named "Call at 3:45 PM, 19 Sep 2026" that will never be used again. They
 * fall inactive after a week of silence and are destroyed a month after that
 * on their own — this screen is for seeing what is queued up and, when a month
 * is too long to wait, clearing it out now.
 *
 * Restoring is the other half of that: a group that mattered gets its week
 * back rather than being destroyed because everybody was on leave.
 *
 * Deadlines are not computed here. They arrive as `expiryState` from the
 * server, which is the same calculation the sweep that does the deleting uses
 * — a second copy in the browser is how a list promises twelve days for
 * something that goes tonight.
 */

const when = (iso) => (iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—');

const InactiveGroups = ({ query = '', onOpenConversation }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/conversations/inactive-groups', {
                params: { status: 'inactive' }
            });
            setGroups(res.data || []);
            // Anything that has gone since the last load must not stay ticked,
            // or the next Delete would send ids the server has already lost.
            setSelected((prev) => new Set((res.data || [])
                .map((g) => String(g._id))
                .filter((id) => prev.has(id))));
        } catch {
            setGroups([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return groups;
        return groups.filter((g) => (g.name || '').toLowerCase().includes(needle));
    }, [groups, query]);

    const allShown = visible.length > 0 && visible.every((g) => selected.has(String(g._id)));

    const toggle = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // Ticks only what the search is currently showing — a select-all that
    // silently included rows filtered off-screen would delete them too.
    const toggleAll = () => setSelected((prev) => {
        if (allShown) {
            const next = new Set(prev);
            visible.forEach((g) => next.delete(String(g._id)));
            return next;
        }
        return new Set([...prev, ...visible.map((g) => String(g._id))]);
    });

    const restore = async (group) => {
        setBusy(true);
        try {
            await api.post(`/conversations/${group._id}/restore`);
            setGroups((prev) => prev.filter((g) => String(g._id) !== String(group._id)));
            Swal.fire({
                icon: 'success',
                title: 'Restored',
                text: `"${group.name}" is active again.`,
                timer: 1800,
                showConfirmButton: false
            });
        } catch (err) {
            Swal.fire('Could not restore', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setBusy(false);
        }
    };

    const deleteSelected = async () => {
        const ids = [...selected];
        if (!ids.length) return;

        /*
         * Spelled out rather than a bare "Are you sure?".
         *
         * This is the one irreversible thing on the screen, and what goes with
         * a group is not obvious from looking at it — the photos, the voice
         * notes and the recordings shared in it go too, for everybody who was
         * in it.
         */
        const confirmed = await Swal.fire({
            icon: 'warning',
            title: `Delete ${ids.length} group${ids.length > 1 ? 's' : ''}?`,
            html: 'Their messages and everything shared in them — photos, files, '
                + 'voice notes and recordings — are removed permanently, for everyone. '
                + 'This cannot be undone.',
            showCancelButton: true,
            confirmButtonText: 'Delete permanently',
            confirmButtonColor: '#dc2626'
        });
        if (!confirmed.isConfirmed) return;

        setBusy(true);
        try {
            const res = await api.post('/conversations/inactive-groups/delete', { ids });
            const { count = 0, failed = [] } = res.data || {};
            setSelected(new Set());
            await load();

            if (failed.length) {
                Swal.fire({
                    icon: 'warning',
                    title: `${count} deleted, ${failed.length} could not be`,
                    text: failed[0]?.message || 'Some groups were left in place.'
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: `${count} group${count === 1 ? '' : 's'} deleted`,
                    timer: 1800,
                    showConfirmButton: false
                });
            }
        } catch (err) {
            Swal.fire('Could not delete', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="msgr-empty">Loading…</div>;

    if (!visible.length) {
        return (
            <div className="msgr-empty">
                {query.trim()
                    ? 'No inactive groups match that.'
                    : 'Nothing has gone inactive. Call groups appear here after a week of silence.'}
            </div>
        );
    }

    return (
        <>
            <div className="msgr-inactive-bar">
                <button type="button" className="msgr-inactive-selectall" onClick={toggleAll}>
                    <FontAwesomeIcon icon={faCheckDouble} />
                    {allShown ? 'Clear selection' : 'Select all'}
                </button>
                <span className="msgr-inactive-count">
                    {selected.size ? `${selected.size} selected` : `${visible.length} inactive`}
                </span>
                <button
                    type="button"
                    className="msgr-inactive-delete"
                    disabled={!selected.size || busy}
                    onClick={deleteSelected}
                >
                    <FontAwesomeIcon icon={busy ? faSpinner : faTrash} spin={busy} />
                    Delete
                </button>
            </div>

            <div className="msgr-list">
                {visible.map((g) => {
                    const id = String(g._id);
                    const state = g.expiryState;
                    const urgent = state && state.daysUntilDelete <= 7;

                    return (
                        <div key={id} className={`msgr-row msgr-inactive-row ${selected.has(id) ? 'is-picked' : ''}`}>
                            <input
                                type="checkbox"
                                className="msgr-inactive-check"
                                checked={selected.has(id)}
                                onChange={() => toggle(id)}
                                aria-label={`Select ${g.name}`}
                            />

                            <GroupIcon conversation={g} className="msgr-avatar" />

                            <div
                                className="msgr-row-body"
                                onClick={() => onOpenConversation?.(g)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && onOpenConversation?.(g)}
                            >
                                <div className="msgr-row-top">
                                    <span className="msgr-row-name">{g.name}</span>
                                    <span className={`msgr-tag ${urgent ? 'is-danger' : 'is-warning'}`}>
                                        <FontAwesomeIcon icon={faHourglassHalf} /> {deadlineLabel(state)}
                                    </span>
                                </div>
                                <div className="msgr-row-bottom">
                                    <span className="msgr-row-preview">
                                        {g.memberCount} member{g.memberCount === 1 ? '' : 's'}
                                        {' · '}silent since {when(g.lastActivityAt)}
                                    </span>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="msgr-icon-btn"
                                title="Restore — gives it another week"
                                disabled={busy}
                                onClick={() => restore(g)}
                            >
                                <FontAwesomeIcon icon={faRotateLeft} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default InactiveGroups;
